import { ViewModel } from './view-model';
import { AggregateMetaData, MetaDataUtils } from '../meta-data';
import { ExternalViewModelTypeInspector } from './decorators/external-view-model-type.decorator';
import type { ModelChangedEventArgsInterface, IdentityInterface, ModelInterface, AggregateMetaDataInterface, ClassInformationInterface, CoreOrchestratorViewModelInterface, ExternalRetrieverInterface, ViewModelEventDispatcherInterface, ExternalViewModelInterface, ViewModelInterface, InternalViewModelInterface, CollectionViewModelInterface, DomainModelCollectionInterface, InternalRelationMetaDataInterface, ExternalMetaDataInterface, InitInternalViewModelArguments } from '@nts/std/interfaces';
import { ViewModelFactory } from './view-model-factory';
import { ExternalDomainModelChangedEventArgs } from './external-domain-model-changed-event-args';
import { InternalViewModelTypeInspector } from './decorators/internal-view-model-type.decorator';
import { CollectionViewModelTypeInspector } from './decorators/collection-view-model-type.decorator';
import { ModelTypeInspector, ExternalInspector } from '../domain-models';
import { LogService } from '@nts/std/utility';
import { ExternalDomainModelTypeInspector } from './decorators/external-domain-model-type.decorator';
import { TypeMetadata } from '@nts/std/serialization';
import { DEFAULT_METADATA_STORAGE } from '@nts/std/serialization';
import { ClassConstructor } from '@nts/std/serialization';
import { TypeHelpOptions } from '@nts/std/serialization';
import { CustomCollectionViewModelInspector } from './decorators/custom-collection-view-model.decorator';
import { ClassInformationType, CloneTypes } from '@nts/std/types';
import { DynamicInternal } from "../domain-models/dynamic-internal";
import { DynamicInternalIdentity } from "../domain-models/dynamic-internal.identity";
import { MessageResourceManager } from '../resources/message-resource-manager';
import { InternalRelationMetaData } from '../meta-data/internal-relation-meta-data';
import { EventEmitter } from '@angular/core';
import { CollectionViewModel } from './collection-view-model';
import { ModelChangedEventArgs } from './domain-model-changed-event-args';
import { takeUntil } from 'rxjs';

export interface InitAggregateElementViewModelArguments<
  TIdentity extends IdentityInterface = IdentityInterface,
  TModel extends ModelInterface<TIdentity> = ModelInterface<TIdentity>,
> {
  domainModel: TModel,
  aggregateMetaData: AggregateMetaDataInterface,
  orchestratorViewModel: CoreOrchestratorViewModelInterface,
  domainModelName?: string,
  isMock?: boolean,
  domainModelType?: ClassConstructor<TModel>,
  processedNulledModel?: any[];
}

export class AggregateElementViewModel<
  TModel extends ModelInterface<TIdentity>,
  TIdentity extends IdentityInterface>
  extends ViewModel<TModel, TIdentity> {

  protected getExternalViewModelType(propertyName: string): any {
    return ExternalViewModelTypeInspector.getValue(this, propertyName);
  }

  protected getInternalViewModelType(propertyName: string): any {
    return InternalViewModelTypeInspector.getValue(this, propertyName);
  }

  protected getCollectionViewModelType(propertyName: string): any {
    return CollectionViewModelTypeInspector.getValue(this, propertyName);
  }

  protected async initAggregateElementViewModel({
    domainModel,
    aggregateMetaData: aggregateMetadata,
    orchestratorViewModel,
    domainModelName = undefined,
    isMock = false,
    domainModelType = undefined,
    processedNulledModel = []
  }: InitAggregateElementViewModelArguments<TIdentity, TModel>): Promise<void> {

    await this.initViewModel(domainModel, aggregateMetadata, orchestratorViewModel, domainModelName, domainModelType);

    if (!this.domainModelMetaData) {
      let name = domainModelName;
      name = name?.length > 0 ? name : domainModel?.modelTypeName;
      name = name?.length > 0 ? name : 'sconosciuto';
      const fullPath = [this.reservedPath, this.reservedName].filter((w) => w?.length > 0).join('.');
      console.error(`Non è stato trovato il domainModelMetaData di ${fullPath} (${name})!`);
    }

    // Popolo gli eventuali Internal ViewModels
    if (this.domainModelMetaData?.internalRelations.length > 0) {
      await this.buildInternalViewModels(isMock);
    }

    // Popolo gli eventuali Collection ViewModels
    if (this.domainModelMetaData?.internalCollections.length > 0 && domainModel) {
      await this.buildInternalCollectionViewModels(isMock);
    }

    // Popolo i dati relativi alle decodifiche (External)
    // Verifico eventuali refernze circolari quando il modello è nullo, al momento è stato impostato un limite di 2 volte per lo stesso modello
    if (this.domainModelMetaData?.externals.length > 0 && (domainModel || processedNulledModel.filter(x => x === this.domainModelMetaData.fullName).length < 2)) {
      if (!domainModel) {
        processedNulledModel.push(this.domainModelMetaData.fullName);
      }
      await this.buildExternalViewModels(isMock, false, processedNulledModel);
    }

    this.relationViewModels.forEach(x => {
      x.parent = this;
    });
  }

  protected initCustomAggregateElementViewModel(
    domainModel: TModel,
    aggregateMetadata: AggregateMetaData,
    domainModelName: string = undefined,
    domainModelType: ClassConstructor<TModel> = null,
    externalRetriever?: ExternalRetrieverInterface,
    eventDispatcher?: ViewModelEventDispatcherInterface,
  ) {

    this.initCustomViewModel(domainModel, aggregateMetadata, domainModelName, domainModelType, externalRetriever, eventDispatcher);

    if (!this.domainModelMetaData) {
      console.error(`Non è stato trovato il domainModelMetaData di ${domainModelName}!`);
    }

    // Popolo gli eventuali Internal ViewModels
    // if (this.domainModelMetaData.internalRelations.length > 0) {
    //     this.buildInternalViewModels(isMockedViewModel);
    // }

    // // Popolo gli eventuali Collection ViewModels
    // if (this.domainModelMetaData.internalCollections.length > 0 && domainModel) {
    //     await this.buildInternalCollectionViewModels(isMockedViewModel);
    // }

    // Popolo i dati relativi alle decodifiche (External)
    // Verifico eventuali refernze circolari quando il modello è nullo, al momento è stato impostato un limite di 2 volte per lo stesso modello
    // if (this.domainModelMetaData.externals.length > 0 && (domainModel || processedNulledModel.filter(x => x === this.domainModelMetaData.fullName).length < 2)) {
    //     if (!domainModel) {
    //         processedNulledModel.push(this.domainModelMetaData.fullName);
    //     }
    //     this.buildExternalViewModels(isMockedViewModel, false, processedNulledModel);
    // }

    // this.relationViewModels.forEach(x => {
    //     x.parent = this;
    // });
  }

  protected async buildExternalViewModels(
    isMock = false,
    forceCreateViewModels = false,
    processedNulledModel: string[] = []
  ) {

    for (const externalMetaData of this.domainModelMetaData.externals.filter((() => (em: ExternalMetaDataInterface) => em.isExtension === false))) {
      const propertyName = MetaDataUtils.toCamelCase(externalMetaData.principalPropertyName);

      if (!externalMetaData.isExtension) {
        // TODO
        //     if (Properties.ContainsKey(externalRelation.PropertyName))
        //     {
        //         //Verifico se esiste l'entity nell'aggregato
        //         if (entityProperties.ContainsKey(externalRelation.PropertyName))
        //         {



        // Creo il DecodeViewModel
        const externalViewModelType = this.getExternalViewModelType(propertyName);
        let externalModelType: ClassConstructor<ModelInterface> = null;

        if (externalViewModelType != null) {

          externalModelType = ExternalDomainModelTypeInspector.getValue(externalViewModelType);
          if (externalMetaData.cloneType === CloneTypes.LocalReplica && this.domainModel[propertyName] == null) {
            this.domainModel.emitChanges = false;
            this.domainModel[propertyName] = new externalModelType();
            this.domainModel.emitChanges = true;
          }
        }

        const externalDomainModel: ModelInterface = (this.domainModel && this.domainModel[propertyName]) ? this.domainModel[propertyName] : null;

        if (externalViewModelType !== undefined) {

          const mergedDecoratorData = ExternalInspector.getValue(this.domainModelType, propertyName);
          const externalViewModel = await ViewModelFactory.createExternalViewModel(
            externalViewModelType,
            externalDomainModel,
            this.aggregateMetaData,
            this.modifiedSubscriber as CoreOrchestratorViewModelInterface,
            externalMetaData,
            [this.reservedPath, this.reservedName].filter((w) => w?.length > 0).join('.'),
            isMock,
            externalModelType,
            mergedDecoratorData,
            processedNulledModel
          );
          externalViewModel.parent = this;
          this[propertyName] = externalViewModel;

          this.relationViewModels.set(MetaDataUtils.toCamelCase(externalMetaData.principalPropertyName), externalViewModel);
          externalViewModel.externalDomainModelChanged.subscribe((arg) => this.externalEntityChangedHandler(arg, externalViewModel));

          // TODO
          // var item = evm as ISupportHighlight;
          // if (item != null)
          // {
          // 	EntityPropertiesMap.Add(p.Name, item);
          // }

          // se il suo backing field è popolato ma il suo ref non lo è eseguo una setcodevalue che mi permette di popolare correttamente il suo ref
          if (isMock === false && externalMetaData.associationProperties?.length === 1) {
            const backingFieldPropertyName = MetaDataUtils.toCamelCase(externalMetaData.associationProperties[0].principalPropertyName);
            const backingField = this.getProperty(backingFieldPropertyName);
            const extVm = this[propertyName] as ExternalViewModelInterface;

            if (extVm.checkAndFixNullExternal) {
              const extFieldPropertyName = MetaDataUtils.toCamelCase(externalMetaData.associationProperties[0].dependentPropertyName);
              const extField = extVm?.getProperty(extFieldPropertyName);

              if (
                // se il backing field è diverso da null
                backingField?.value != null &&
                (
                  // il suo external è nullo
                  extField?.value == null ||  //o
                  // è diverso dal suo backing field
                  backingField?.value != extField?.value
                )) {
                // Se l'identity è valida
                if (extVm.identityIsValid({ [backingField.propertyMetaData.name]: backingField.value })) {
                  const oldValue1 = extVm.backingFieldCanNotifyModified;
                  const oldValue2 = extField.canNotifyModified;
                  extVm.backingFieldCanNotifyModified = false;
                  extField.canNotifyModified = false;
                  await extVm.setCodeValue(backingField?.value);
                  extVm.backingFieldCanNotifyModified = oldValue1;
                  extField.canNotifyModified = oldValue2;

                }

              }
            }


          }

        } else {
          // potrebbe anche esserci una ExternalList abbinata a questa entity; in tal caso non occorre fare nulla
          // in quanto il ViewModel è già sato creato quando viene invocato il metodo GetExternalListPropertyViewModel
          // throw new InvalidOperationException
          // ("In the class '" + this.GetType().Name + "' the type of property '" + externalRelation.PropertyName + "' must be descendant of 'ExternalViewModel'");
          // throw new Error('INVALID PROPERTY');
        }
      }
    }

    await this.extension.buildExternalViewModels(isMock);
  }

  protected async buildInternalViewModels(
    isMock = false,
  ) {

    for (const internalRelation of this.domainModelMetaData.internalRelations.filter((() => (ir: InternalRelationMetaDataInterface) => ir.isExtension === false))) {

      // TODO
      // //Verifico se questa internalRelation è stata esposta nel ViewModel e se esiste l'entity nell'aggregato
      // if (Properties.ContainsKey(internalRelation.PropertyName) &&
      //         entityProperties.ContainsKey(internalRelation.PropertyName))

      // PATCH PER camelCase DEVE ESSERE PascalCase
      const propertyName = MetaDataUtils.toCamelCase(internalRelation.principalPropertyName);

      const internalModel: TModel = this.domainModel[propertyName];
      if (internalModel != null) {

        // Recupero il tipo del modello dell'internal dal decoratore @Type
        let internalModelType = null;
        const typeMetadata: TypeMetadata = DEFAULT_METADATA_STORAGE.findTypeMetadata(this.domainModelType, propertyName);
        if (typeMetadata && typeMetadata.typeFunction({
          newObject: new this.domainModelType()
        } as any)) {
          internalModelType = typeMetadata.typeFunction({
            newObject: new this.domainModelType()
          } as TypeHelpOptions) as ClassConstructor<any>;
        }

        const internalViewModelType = this.getInternalViewModelType(propertyName);
        if (internalViewModelType != null) {
          const ivm = await ViewModelFactory.createInternalViewModel<TIdentity, TModel>({
            internalViewModelType,
            internalModel,
            aggregateMetaData: this.aggregateMetaData,
            orchestratorViewModel: this.modifiedSubscriber as CoreOrchestratorViewModelInterface,
            relationMetadata: internalRelation,
            skipPostInit: false,
            path: [this.reservedPath, this.reservedName].filter((w) => w?.length > 0).join('.'),
            isMock: isMock,
            parent: this,
            internalModelType,
          });
          this[propertyName] = ivm;
          this.relationViewModels.set(propertyName, ivm as ViewModelInterface);
          ivm.internalDomainModelChanged.subscribe((arg) => this.internalEntityChangedHandler(arg, ivm));

          ivm.viewModelChanged.pipe(takeUntil(ivm.destroySubscribers$)).subscribe(propertyName => {
            this.viewModelChangeDebouncer.next(propertyName);
          });
        } else {
          // throw new InvalidOperationException
          // ("In the class '" + this.GetType().Name + "' the type of property '" + internalRelation.PropertyName + "' must be descendant of 'InternalViewModel'");

          // TODO: Da riattivare dopo il controllo di esistenza della prop nel viewmodel
          // throw new Error('INVALID PROPERTY');
          LogService.warn(`Campo '${propertyName}' non trovato nel view model ${this.constructor.name}`);
        }

      } else {
        // TODO: la entity è null, valutare cosa fare
        // Credo che la entity abbia un metodo per creare una entity vuota, di default:
        // la cosa giusta è forse creare un ViewModel passando quella di default
      }

    }

    await this.extension.buildInternalViewModels(
      isMock
    );

    // TODO: resta il caso ManyToMany: non so come va trattato
  }

  protected async buildInternalCollectionViewModels(isMock: boolean) {

    for (const internalCollection of this.domainModelMetaData.internalCollections) {

      // PATCH PER camelCase DEVE ESSERE PascalCase
      const propertyName = MetaDataUtils.toCamelCase(internalCollection.principalPropertyName);
      const collection = this.domainModel[propertyName];

      if (collection !== undefined) {

        // Creo il ViewModel
        const collectionViewModelType = this.getCollectionViewModelType(propertyName);

        // Recupero il tipo del modello della collection dal decoratore @Type
        let collectionModelType = null;
        const typeMetadata: TypeMetadata = DEFAULT_METADATA_STORAGE.findTypeMetadata(this.domainModelType, propertyName);
        if (typeMetadata && typeMetadata.typeFunction({
          newObject: new this.domainModelType()
        } as any)) {
          collectionModelType = typeMetadata.typeFunction({
            newObject: new this.domainModelType()
          } as TypeHelpOptions) as ClassConstructor<any>;
        }

        // Recupero il modello dell'item della collection dal suo decoratore
        let itemModelType = null;
        if (collectionModelType) {
          itemModelType = ModelTypeInspector.getValue(collectionModelType);
        }

        // itemModelType = collection;
        if (collectionViewModelType !== undefined) {
          const ivm = await ViewModelFactory.createCollectionViewModel(
            collectionViewModelType,
            collection,
            this.aggregateMetaData,
            this.modifiedSubscriber as CoreOrchestratorViewModelInterface,
            internalCollection,
            [this.reservedPath, this.reservedName].filter((w) => w?.length > 0).join('.'),
            isMock,
            itemModelType
          );
          this[propertyName] = ivm;
          this.relationViewModels.set(propertyName, ivm);
          ivm.collectionDomainModelChanged.subscribe((arg) => this.collectionEntityChangedHandler(arg, ivm));

          // ivm.collectionChanged.subscribe(() => {
          //     if ((this.externalRetriever as OrchestratorViewModelInterface).currentState.value !== ViewModelStates.Research) {
          //         this.validate();
          //     }
          // });
        } else {
          // throw new InvalidOperationException
          // ("In the class '" + this.GetType().Name + "' the type of property '" + internalRelation.PropertyName + "' must be descendant of 'InternalViewModel'");

          // TODO: Da riattivare dopo il controllo di esistenza della prop nel viewmodel
          // throw new Error('INVALID PROPERTY');
          LogService.warn(`Campo '${propertyName}' non trovato nel view model ${this.constructor.name}`);
        }

        // PropertyInfo viewModelProperty = Properties[internalRelation.PropertyName];
        // if (viewModelProperty.PropertyType.BaseType.UnderlyingSystemType.Name.StartsWith("EntityCollectionViewModel"))
        // {
        //     var cvm = ViewModelFactory.CreateEntityCollectionViewModel
        // (viewModelProperty.PropertyType, (IEntityCollection)entityCollection, Metadata, (IDocumentViewModel)ModifiedSubsciber, internalRelation);
        //     viewModelProperty.SetValue(this, cvm);
        //     ChildViewModel.Add((IViewModel)cvm);
        // }
        // else
        // {
        //     throw new InvalidOperationException
        // ("In the class '" + this.GetType().Name + "' the type of property '"
        //  + internalRelation.PropertyName + "' must be descendant of 'EntityCollectionViewModel'");
        // }
      } else {
        // TODO: la entity è null, valutare cosa fare
        // Credo che la entity abbia un metodo per creare una entity vuota, di default:
        // la cosa giusta è forse creare un ViewModel passando quella di default
      }
    }

    const customCollectionViewModelsMetaData = this.getCustomCollectionViewModelsMetaData();

    for (const customInternalCollectionMetaData of customCollectionViewModelsMetaData) {

      const propertyName = customInternalCollectionMetaData.principalPropertyName;

      // recupero dal decoratore il nome del della property del modello
      const customCollectionViewModelDecoratorData = CustomCollectionViewModelInspector.getValue(this, propertyName);
      const domainPropertyName = customCollectionViewModelDecoratorData.domainPropertyName;
      const collection = this.domainModel[domainPropertyName];

      if (collection !== undefined) {

        // Creo il ViewModel
        const collectionViewModelType = this.getCollectionViewModelType(propertyName);

        // Recupero il tipo del modello della collection dal decoratore @Type
        let collectionModelType = null;
        const typeMetadata: TypeMetadata = DEFAULT_METADATA_STORAGE.findTypeMetadata(this.domainModelType, domainPropertyName);
        if (typeMetadata && typeMetadata.typeFunction({
          newObject: new this.domainModelType()
        } as any)) {
          collectionModelType = typeMetadata.typeFunction({
            newObject: new this.domainModelType()
          } as TypeHelpOptions) as ClassConstructor<any>;
        }

        // Recupero il modello dell'item della collection dal suo decoratore
        let itemModelType = null;
        if (collectionModelType) {
          itemModelType = ModelTypeInspector.getValue(collectionModelType);
        }

        // itemModelType = collection;
        if (collectionViewModelType !== undefined) {
          const ivm = await ViewModelFactory.createCollectionViewModel(
            collectionViewModelType,
            collection,
            this.aggregateMetaData,
            this.modifiedSubscriber as CoreOrchestratorViewModelInterface,
            customInternalCollectionMetaData,
            [this.reservedPath, this.reservedName].filter((w) => w?.length > 0).join('.'),
            isMock,
            itemModelType
          );
          this[propertyName] = ivm;
          this.relationViewModels.set(propertyName, ivm);
          // ivm.collectionChanged.subscribe(() => {
          //     if ((this.externalRetriever as OrchestratorViewModelInterface).currentState.value !== ViewModelStates.Research) {
          //         this.validate();
          //     }
          // });
        } else {
          // throw new InvalidOperationException
          // ("In the class '" + this.GetType().Name + "' the type of property '" + internalRelation.PropertyName + "' must be descendant of 'InternalViewModel'");

          // TODO: Da riattivare dopo il controllo di esistenza della prop nel viewmodel
          // throw new Error('INVALID PROPERTY');
          LogService.warn(`Campo '${propertyName}' non trovato nel view model ${this.constructor.name}`);
        }

        // PropertyInfo viewModelProperty = Properties[internalRelation.PropertyName];
        // if (viewModelProperty.PropertyType.BaseType.UnderlyingSystemType.Name.StartsWith("EntityCollectionViewModel"))
        // {
        //     var cvm = ViewModelFactory.CreateEntityCollectionViewModel
        // (viewModelProperty.PropertyType, (IEntityCollection)entityCollection, Metadata, (IDocumentViewModel)ModifiedSubsciber, internalRelation);
        //     viewModelProperty.SetValue(this, cvm);
        //     ChildViewModel.Add((IViewModel)cvm);
        // }
        // else
        // {
        //     throw new InvalidOperationException
        // ("In the class '" + this.GetType().Name + "' the type of property '"
        //  + internalRelation.PropertyName + "' must be descendant of 'EntityCollectionViewModel'");
        // }
      } else {
        // TODO: la entity è null, valutare cosa fare
        // Credo che la entity abbia un metodo per creare una entity vuota, di default:
        // la cosa giusta è forse creare un ViewModel passando quella di default
      }
    }

    // TODO: resta il caso ManyToMany: non so come va trattato
  }

  protected externalEntityChangedHandler(arg: ExternalDomainModelChangedEventArgs, externalViewModel: ExternalViewModelInterface): void {
    if (this.domainModel) {
      const name = arg.senderName;
      // assegnamento del nuovo domain model esterno
      // this.domainModel[name] = arg.domainModel;
      this.domainModel.setPropertyValue(name, arg.domainModel);

      externalViewModel.validate();

      this.viewModelChangeDebouncer.next(name);
    }
  }

  protected internalEntityChangedHandler(arg: ModelChangedEventArgsInterface<ModelInterface>, internalViewModel: InternalViewModelInterface): void {
    if (this.domainModel) {
      const name = arg.senderName;

      this.domainModel.setPropertyValue(name, arg.model);

      internalViewModel.validate();

      this.viewModelChangeDebouncer.next(name);
    }
  }

  protected collectionEntityChangedHandler(
    arg: ModelChangedEventArgsInterface<DomainModelCollectionInterface>,
    collectionViewModel: CollectionViewModelInterface
  ): void {
    if (this.domainModel) {
      const name = arg.senderName;

      this.domainModel.setPropertyValue(name, arg.model);

      collectionViewModel.validate();

      this.viewModelChangeDebouncer.next(name);
    }
  }
}

export class InternalViewModel<TModel extends ModelInterface<TIdentity>, TIdentity extends IdentityInterface>
  extends AggregateElementViewModel<TModel, TIdentity> implements InternalViewModelInterface<TIdentity, TModel>, ClassInformationInterface {

  classType = ClassInformationType.InternalViewModel;
  relationMetaData: InternalRelationMetaData;
  internalDomainModelChanged = new EventEmitter<ModelChangedEventArgsInterface<TModel>>();

  async initInternalViewModel({
    domainModel,
    aggregateMetaData,
    orchestratorViewModel,
    relationMetaData,
    path,
    isMock,
    parent = undefined,
    domainModelType = undefined
  }: InitInternalViewModelArguments<TIdentity, TModel>): Promise<void> {

    this.relationMetaData = relationMetaData;
    this.reservedPath = path;
    this.reservedName = MetaDataUtils.toCamelCase(relationMetaData.principalPropertyName);
    this.isMock = isMock;
    this.parent = parent;

    await this.initAggregateElementViewModel({
      domainModel,
      aggregateMetaData,
      orchestratorViewModel,
      isMock,
      domainModelType
    });

    this.metadataShortDescription = orchestratorViewModel?.useMessageResourceKey ?
      MessageResourceManager.Current.getMessageIfExists(
        relationMetaData.dependentMetaData.descriptions.displayNameKey) : relationMetaData.dependentMetaData.descriptions.displayName;

    this.metadataDescription = orchestratorViewModel?.useMessageResourceKey ?
      MessageResourceManager.Current.getMessageIfExists(
        relationMetaData.dependentMetaData.descriptions.descriptionKey) : relationMetaData.dependentMetaData.descriptions.description;
  }

  async setFromModel(newDomainModel: TModel) {
    for (const [key, pvm] of this.propertyViewModels) {
      pvm.setModel(newDomainModel);
    }

    for (const [relationKey, relation] of this.relationViewModels) {
      if (
        (relation as ClassInformationInterface)?.classType === ClassInformationType.ExternalViewModel
        || relation instanceof InternalViewModel
      ) {
        const relationViewModel = relation as unknown as { setFromModel: (model: unknown) => Promise<void>; };
        if (newDomainModel && newDomainModel[relationKey]) {
          await relationViewModel.setFromModel(newDomainModel[relationKey]);
        } else {
          await relationViewModel.setFromModel(null);
        }
      } else if (relation instanceof CollectionViewModel) {
        if (newDomainModel && newDomainModel[relationKey]?.collectionItems) {
          await relation.setFromEntities(newDomainModel[relationKey]?.collectionItems);
        } else {
          await relation.setFromEntities([]);
        }
      }
    }

    if (this.domainModel !== newDomainModel) {
      this.domainModel = newDomainModel;

      const args = new ModelChangedEventArgs<TModel>();
      args.model = this.domainModel;
      args.senderName = this.reservedName;
      this.internalDomainModelChanged.emit(args);

      this.validate();
    } else if (this.domainModel == null) {
      this.validate();
    }
  }
}
export class DynamicInternalViewModel extends InternalViewModel<DynamicInternal, DynamicInternalIdentity> {

}
